import { FC, useState, useEffect, CSSProperties, useContext } from "react";
import { Button, FormControl, Modal, Table } from "react-bootstrap";
import _ from "lodash";
import { useCSVReader } from "react-papaparse";
import timesheetApi from "../../../api/timesheetApi";
import { AppContext } from "../../../context";
import { ClockInType } from "../../../types/ClockInType";
import { User } from "../../../types/User";
import Variable from "../../../utils/Variable";

interface Props {
  user: User;
  visible: boolean;
  onVisibleChange: (val: boolean) => void;
}

const styles = {
  csvReader: {
    display: "flex",
    flexDirection: "row",
    marginBottom: 10,
  } as CSSProperties,
  browseFile: {
    width: "20%",
  } as CSSProperties,
  acceptedFile: {
    border: "1px solid #ccc",
    height: 45,
    lineHeight: 2.5,
    paddingLeft: 10,
    width: "80%",
  } as CSSProperties,
  remove: {
    borderRadius: 0,
    padding: "0 20px",
  } as CSSProperties,
  progressBarBackgroundColor: {
    backgroundColor: "red",
  } as CSSProperties,
};

const selectableColumns = ["clockInTypeId", "date", "startTime", "endTime", "minutes", "projectId", "note"] as const;

type SelectedColumnType = (typeof selectableColumns)[number];

type SelectedColumn = {
  type: SelectedColumnType;
  index: number;
};

const ImportHoursModal: FC<Props> = ({ user, visible, onVisibleChange }) => {
  const { showToast } = useContext(AppContext);
  const { CSVReader } = useCSVReader();
  const [selectedColumns, setSelectedColumns] = useState<SelectedColumn[]>([]);
  const [data, setData] = useState([]);
  const [clockInTypes, setClockInTypes] = useState<ClockInType[]>([]);

  useEffect(() => {
    if (visible) {
      setData([]);
      setSelectedColumns([]);
      if (user.companyId) {
        timesheetApi.getClockInTypes(user.companyId).then((res) => setClockInTypes(res.data));
      }
    }
  }, [visible]);

  return (
    <Modal show={visible} onHide={() => onVisibleChange(false)} size="xl">
      <Modal.Header closeButton={true} className="d-flex justify-content-between">
        <Modal.Title className="flex-grow-1">Import timesheets for user</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {!data.length ? (
          <CSVReader
            onUploadAccepted={(results: any) => {
              setData(results.data);
            }}>
            {({ getRootProps, acceptedFile, ProgressBar, getRemoveFileProps }: any) => (
              <>
                <div style={styles.csvReader}>
                  <button type="button" {...getRootProps()} style={styles.browseFile}>
                    Browse file
                  </button>
                  <div style={styles.acceptedFile}>{acceptedFile && acceptedFile.name}</div>
                  <button {...getRemoveFileProps()} style={styles.remove}>
                    Remove
                  </button>
                </div>
                <ProgressBar style={styles.progressBarBackgroundColor} />
              </>
            )}
          </CSVReader>
        ) : (
          <>
            <Table responsive striped>
              <thead>
                <tr>
                  {data[0].map((__, i) => {
                    const selected = selectedColumns.find((sc) => sc.index === i);
                    return (
                      <th key={i}>
                        <FormControl
                          value={selected?.type}
                          as="select"
                          className={selected ? "border-success" : ""}
                          style={{ minWidth: 140 }}
                          onChange={(e) => {
                            if (selected) {
                              setSelectedColumns(selectedColumns.filter((sc) => sc.index !== i));
                            } else {
                              setSelectedColumns([
                                ...selectedColumns,
                                {
                                  type: e.target.value as SelectedColumnType,
                                  index: i,
                                },
                              ]);
                            }
                          }}>
                          <option value={""}>{"Select"}</option>
                          {selectableColumns.map((c) => {
                            return (
                              <option value={c} key={c} disabled={selectedColumns.some((sc) => sc.type === c)}>
                                {c}
                              </option>
                            );
                          })}
                        </FormControl>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {[...data].slice(1, data.length).map((row, i) => {
                  return (
                    <tr key={i}>
                      {row.map((d, colIndex) => {
                        const col = selectedColumns.find((sc) => sc.index === colIndex);
                        if (col?.type === "clockInTypeId" && d) {
                          const cit = clockInTypes.find((c) => c.id === d);
                          return <td key={colIndex}>{cit ? cit.titleEn : d}</td>;
                        }
                        return <td key={colIndex}>{d}</td>;
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </>
        )}
      </Modal.Body>
      <Modal.Footer style={{ position: "sticky", bottom: 0 }} className="bg-white">
        <Variable initialValue={false}>
          {(loading, toggleLoading) => {
            return (
              <Button
                variant="primary"
                disabled={!selectedColumns.length || loading}
                onClick={async () => {
                  toggleLoading(true);
                  try {
                    const dataWithoutHeader = data.slice(1, data.length);

                    const mappedData = dataWithoutHeader.map((row) => {
                      const scs = row
                        .map((v, i) => {
                          const selected = selectedColumns.some((sc) => sc.index === i);
                          if (!selected) {
                            return undefined;
                          }
                          return { index: i, value: v };
                        })
                        .filter((v) => !!v);

                      const baseObject = {};

                      scs.forEach((val) => {
                        const sc = selectedColumns.find((sc) => sc.index === val.index);
                        _.assign(baseObject, {
                          [sc.type]: sc.type === "minutes" ? Number(val.value) : val.value,
                        });
                      });

                      return baseObject;
                    });

                    await timesheetApi.batchCreateTimesheets({
                      userId: user.id,
                      timesheets: mappedData,
                    });
                    onVisibleChange(false);
                    showToast({
                      variant: "success",
                      title: "Success",
                      message: "Timesheets created successfully",
                    });
                  } catch (e) {
                    const isError = e instanceof Error;
                    showToast({
                      variant: "danger",
                      title: isError ? e.name : "Save failed",
                      message: isError ? e.message : "Unkown stuff",
                    });
                  }
                  toggleLoading(false);
                }}>
                Create timesheets
              </Button>
            );
          }}
        </Variable>
      </Modal.Footer>
    </Modal>
  );
};

export default ImportHoursModal;
